/**
 * Faculty Master — roster filters (semester & division optional)
 */

import { useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  OPERATIONAL_ACADEMIC_YEAR,
  OPERATIONAL_ODD_SEMESTERS,
  filterOperationalSemesters,
} from '../config/operationalAcademicContext';
import {
  filterDivisionsForStudentContext,
  programBelongsToDepartment,
} from './StudentMasterFilters';

export const FACULTY_ALL_PROGRAMS = '__ALL_PROGRAMS__';

export function filterDivisionsForFacultyContext(divisions, filters, departments, programs) {
  return (divisions || []).filter((d) => {
    const year = d.academic_year ?? d.academicYear;
    if (filters.academicYear && year && year !== filters.academicYear) return false;
    const degId = d.degree_id ?? d.degreeId;
    if (filters.degreeId && filters.degreeId !== FACULTY_ALL_PROGRAMS
      && String(degId) !== String(filters.degreeId)) {
      return false;
    }
    if ((!filters.degreeId || filters.degreeId === FACULTY_ALL_PROGRAMS) && filters.department) {
      const prog = (programs || []).find((p) => String(p.id) === String(degId));
      if (prog && !programBelongsToDepartment(prog, filters.department, departments)) {
        return false;
      }
    }
    if (filters.semester !== '' && filters.semester != null && Number(d.semester) !== Number(filters.semester)) {
      return false;
    }
    return true;
  });
}

export function facultyMatchesHighlight(faculty, term) {
  if (!term || !String(term).trim()) return false;
  const t = String(term).trim().toLowerCase();
  const fullName = `${faculty.first_name || ''} ${faculty.last_name || ''}`.trim().toLowerCase();
  return (
    fullName.includes(t)
    || String(faculty.employee_id || '').toLowerCase().includes(t)
    || String(faculty.email || '').toLowerCase().includes(t)
    || String(faculty.specialization || '').toLowerCase().includes(t)
  );
}

export default function FacultyMasterFilters({
  searchInput,
  onSearchInputChange,
  onSearchApply,
  onSearchClear,
  highlightTerm,
  facultyFilters,
  onFiltersChange,
  departments,
  programs,
  divisions,
  academicYears,
  semesterOptions,
  canShowRoster,
  rosterShown,
  resultCount,
  highlightMatchCount,
  onShowRoster,
  rosterLoading,
  academicYearLocked = false,
}) {
  const departmentName = facultyFilters.department;
  const activePrograms = (programs || []).filter((p) => p.is_active !== false);
  let programOptions = activePrograms.filter((p) => programBelongsToDepartment(p, departmentName, departments));
  if (departmentName && programOptions.length === 0) {
    programOptions = activePrograms;
  }

  const divisionOptions = useMemo(
    () => filterDivisionsForFacultyContext(divisions, facultyFilters, departments, programs),
    [divisions, facultyFilters, departments, programs]
  );

  const semesterList = useMemo(() => {
    let list = semesterOptions?.length
      ? semesterOptions
      : [...OPERATIONAL_ODD_SEMESTERS];
    list = filterOperationalSemesters(list);
    let max = 8;
    if (facultyFilters.degreeId && facultyFilters.degreeId !== FACULTY_ALL_PROGRAMS) {
      max = Number(programs.find((p) => p.id === facultyFilters.degreeId)?.number_of_semesters || 8);
    } else if (departmentName) {
      const deptPrograms = activePrograms.filter((p) => programBelongsToDepartment(p, departmentName, departments));
      max = deptPrograms.reduce((m, p) => Math.max(m, Number(p.number_of_semesters || 8)), 8);
    }
    return list.filter((s) => s <= max);
  }, [semesterOptions, programs, facultyFilters.degreeId, departmentName, departments, activePrograms]);

  const handleDepartmentChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      department: value,
      degreeId: FACULTY_ALL_PROGRAMS,
      semester: '',
      divisionId: '',
    }));
  };

  const handleProgramChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      degreeId: value,
      semester: '',
      divisionId: '',
    }));
  };

  const handleSemesterChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      semester: value === '' ? '' : Number(value),
      divisionId: '',
    }));
  };

  const statusMessage = (() => {
    if (!facultyFilters.academicYear || !facultyFilters.department) {
      return 'Select academic year and department.';
    }
    if (!facultyFilters.degreeId) {
      return 'Choose a program or “All programs” in the department, then click Show.';
    }
    if (!rosterShown) {
      const extra = [];
      if (facultyFilters.semester !== '' && facultyFilters.semester != null) {
        extra.push(`sem ${facultyFilters.semester}`);
      }
      if (facultyFilters.divisionId) {
        extra.push('division');
      }
      const hint = extra.length ? ` Optional: ${extra.join(', ')}.` : ' Semester and division are optional.';
      return `Ready — click Show to load faculty.${hint}`;
    }
    return `Showing ${resultCount ?? 0} faculty member${resultCount === 1 ? '' : 's'}.`;
  })();

  return (
    <div className="student-roster-panel">
      <div className="student-roster-panel-head">
        <div>
          <h3 className="student-roster-title">Filters</h3>
          <p className="student-roster-subtitle">Semester and division narrow allocations; leave blank for all program faculty</p>
        </div>
        <div className="student-roster-search" aria-label="Search loaded faculty">
          <MagnifyingGlassIcon className="student-roster-search-icon" aria-hidden />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearchApply();
              }
            }}
            placeholder="Highlight name, ID, email…"
            className="input student-roster-search-input"
            aria-label="Search faculty"
          />
          <button type="button" className="btn-secondary student-roster-search-go" onClick={onSearchApply}>
            Find
          </button>
          {highlightTerm ? (
            <button type="button" className="btn-secondary student-roster-search-go" onClick={onSearchClear}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {highlightTerm ? (
        <p className="student-roster-highlight-note">
          {highlightMatchCount} match{highlightMatchCount === 1 ? '' : 'es'} for &ldquo;{highlightTerm}&rdquo;
        </p>
      ) : null}

      <div className="student-roster-filters faculty-roster-filters">
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="faculty-filter-ay">Academic year</label>
          {academicYearLocked ? (
            <div id="faculty-filter-ay" className="student-roster-ay-locked">
              {OPERATIONAL_ACADEMIC_YEAR}
            </div>
          ) : (
            <select
              id="faculty-filter-ay"
              value={facultyFilters.academicYear}
              onChange={(e) => onFiltersChange((f) => ({
                ...f,
                academicYear: e.target.value,
                semester: '',
                divisionId: '',
              }))}
              className="input student-roster-select"
            >
              <option value="">Year</option>
              {academicYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
        </div>
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="faculty-filter-dept">Department</label>
          <select
            id="faculty-filter-dept"
            value={facultyFilters.department}
            onChange={(e) => handleDepartmentChange(e.target.value)}
            className="input student-roster-select"
          >
            <option value="">Department</option>
            {departments.map((d) => (
              <option key={d.id || d.name} value={d.name}>{d.name}</option>
            ))}
          </select>
        </div>
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="faculty-filter-program">Program</label>
          <select
            id="faculty-filter-program"
            value={facultyFilters.degreeId}
            onChange={(e) => handleProgramChange(e.target.value)}
            className="input student-roster-select"
            disabled={!facultyFilters.department}
          >
            <option value="">Program</option>
            <option value={FACULTY_ALL_PROGRAMS}>All programs in department</option>
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.program_title || p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="faculty-filter-sem">Semester (opt.)</label>
          <select
            id="faculty-filter-sem"
            value={facultyFilters.semester}
            onChange={(e) => handleSemesterChange(e.target.value)}
            className="input student-roster-select"
            disabled={!facultyFilters.department}
          >
            <option value="">Any</option>
            {semesterList.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="student-roster-field student-roster-field-division">
          <label className="student-roster-label" htmlFor="faculty-filter-div">
            Division (opt.){divisionOptions.length ? ` (${divisionOptions.length})` : ''}
          </label>
          <select
            id="faculty-filter-div"
            value={facultyFilters.divisionId}
            onChange={(e) => onFiltersChange((f) => ({ ...f, divisionId: e.target.value }))}
            className="input student-roster-select"
            disabled={!facultyFilters.department || facultyFilters.semester === '' || facultyFilters.semester == null}
          >
            <option value="">Any</option>
            {divisionOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} (Sem {d.semester})
              </option>
            ))}
          </select>
        </div>
        <div className="student-roster-field student-roster-field-action">
          <span className="student-roster-label student-roster-label-invisible">Show</span>
          <button
            type="button"
            className="btn-primary student-roster-show-btn"
            disabled={!canShowRoster || rosterLoading}
            onClick={onShowRoster}
          >
            {rosterLoading ? 'Loading…' : 'Show'}
          </button>
        </div>
      </div>

      <p className={`student-roster-status ${canShowRoster ? 'student-roster-status-ready' : ''}`} role="status">
        {statusMessage}
      </p>
    </div>
  );
}
